import { apiClient, getPlatformUrl } from "../../lib/api";
import {
  EstimateReq,
  EstimateResult,
  OutcomeParentRanges,
  OutcomeParentRangesByIdCode,
} from "./type/simulation-def";
import {
  EstimationDef,
  EstimationRecordsDef,
  EstimationTaskDef,
  InputUploadDef,
  IpwComputeDef,
  SmdBalanceDef,
} from "./type/batch-def";

/**
 * シミュレーション系エンドポイントのパス。
 *
 * - `outcomeParentRanges`: stg-insight の cate サービス（`/xcausal/cate/openapi.json`）に
 *   実在を確認済み。`asset_id` と `outcome` はいずれも必須のクエリ。
 * - `estimate`: mq サービスに実在を確認済み。**同期 API** で、task_id ではなく
 *   結果（point_estimate / uncertainty_interval / distribution）が直接返る。
 */
/** テンプレートに入れるサンプル行数。 */
export const TEMPLATE_SAMPLE_ROWS = 50;

export const SIMULATION_PATHS = {
  outcomeParentRanges: "/xcausal/ipw/outcome-parent-ranges",
  outcomeParentRangesByIdCode: "/xcausal/ipw/outcome-parent-ranges-by-id-code",
  smdBalance: "/xcausal/ipw/smd-balance",
  ipwCompute: "/xcausal/ipw/compute",
  inputTemplate: (modelId: string) =>
    `/api/message-queue/outcome-estimation/models/${encodeURIComponent(modelId)}/input-template`,
  inputUploads: "/api/message-queue/outcome-estimation/input-uploads",
  estimationTasks: "/api/message-queue/outcome-estimation/tasks",
  estimationTask: (taskId: string) =>
    `/api/message-queue/outcome-estimation/tasks/${encodeURIComponent(taskId)}`,
  estimation: (estimationId: string) =>
    `/api/message-queue/outcome-estimation/estimations/${encodeURIComponent(estimationId)}`,
  estimationRecords: (estimationId: string) =>
    `/api/message-queue/outcome-estimation/estimations/${encodeURIComponent(estimationId)}/records`,
  estimationDownload: (estimationId: string) =>
    `/api/message-queue/outcome-estimation/estimations/${encodeURIComponent(estimationId)}/download`,
  estimate: (modelId: string) =>
    `/api/message-queue/outcome-estimation/models/${encodeURIComponent(modelId)}/estimate`,
};

/** 署名付き URL への PUT が失敗したときのエラー。 */
export class SignedUrlUploadError extends Error {
  /** レスポンスが返る前に遮断されたか（CORS・オフライン等）。 */
  isNetwork: boolean;
  status?: number;
  body?: string;

  constructor(
    message: string,
    options: { isNetwork: boolean; status?: number; body?: string }
  ) {
    super(message);
    this.name = "SignedUrlUploadError";
    this.isNetwork = options.isNetwork;
    this.status = options.status;
    this.body = options.body;
  }
}

export class SimulationGateway {
  /** アウトカムの親変数と、その可動域を取得する。 */
  async fetchOutcomeParentRanges(req: {
    asset_id: string;
    outcome: string;
  }): Promise<OutcomeParentRanges> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.outcomeParentRanges}?${query}`;
    return (await apiClient.get({ url })) as OutcomeParentRanges;
  }

  /**
   * 親変数のレンジを、識別コードごとの実測値つきで取得する。
   *
   * `outcome-parent-ranges` との違いは `all_user_ids` と、各ユーザーの `value`
   * が付くこと。スライダーの初期値をレンジ中央ではなく実測値にできる。
   */
  async fetchOutcomeParentRangesByIdCode(req: {
    asset_id: string;
    outcome: string;
  }): Promise<OutcomeParentRangesByIdCode> {
    const query = new URLSearchParams(req).toString();
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.outcomeParentRangesByIdCode}?${query}`;
    return (await apiClient.get({ url })) as OutcomeParentRangesByIdCode;
  }

  /**
   * 推定を実行する。`mq/` 配下だが**同期**で、結果がそのまま返る。
   *
   * model_id は **dwmodel(asset)**。GCM の id ではない。
   */
  async postEstimate(modelId: string, req: EstimateReq): Promise<EstimateResult> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimate(modelId)}`;
    return (await apiClient.post({
      url,
      body: req,
      useToken: true,
    })) as EstimateResult;
  }

  // --- ② バッチアウトカムシミュレーション -------------------------------

  /**
   * CSV のテンプレートを取得する。
   *
   * `sample_rows` を付けると、ヘッダ行だけでなくサンプル行も入った状態で返る。
   */
  async fetchInputTemplate(
    modelId: string,
    sampleRows = TEMPLATE_SAMPLE_ROWS
  ): Promise<Blob> {
    const query = new URLSearchParams({
      sample_rows: String(sampleRows),
    }).toString();
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.inputTemplate(modelId)}?${query}`;
    return (await apiClient.get({
      url,
      useToken: true,
      responseType: "blob",
    })) as unknown as Blob;
  }

  /** アップロード用の署名付き URL を発行する。 */
  async createInputUpload(req: {
    model_id: string;
    file_name: string;
  }): Promise<InputUploadDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.inputUploads}`;
    return (await apiClient.post({
      url,
      body: req,
      useToken: true,
    })) as InputUploadDef;
  }

  /**
   * 署名付き URL へ CSV を直接 PUT する。
   *
   * **apiClient は使えない。** URL 自体が署名を持っており、`Authorization` を
   * 付けると S3 が 403 を返す。body も JSON ではなく File/Blob をそのまま送る
   * （FormData / multipart は不可）。
   */
  async uploadToSignedUrl(
    uploadUrl: string,
    file: File | Blob,
    headers: Record<string, string>
  ): Promise<void> {
    let res: Response;
    try {
      res = await fetch(uploadUrl, { method: "PUT", headers, body: file });
    } catch (error) {
      // レスポンスが返る前に落ちた＝ブラウザが遮断した。
      // `Content-Type: text/csv` はセーフリスト外なのでプリフライトが飛ぶため、
      // バケットに CORS が無いとここに来る（ステータスも本文も取れない）。
      throw new SignedUrlUploadError(
        `upload blocked before reaching S3: ${String(error)}`,
        { isNetwork: true }
      );
    }

    if (!res.ok) {
      // S3 はエラーの詳細を XML の本文で返すので、拾って原因を読めるようにする
      const body = await res.text().catch(() => "");
      throw new SignedUrlUploadError(
        `upload failed: ${res.status} ${res.statusText}${body ? `\n${body}` : ""}`,
        { isNetwork: false, status: res.status, body }
      );
    }
  }

  /**
   * 推定タスクを作る。① の `estimate` と違い**非同期**で、task_id が返る。
   *
   * `gcm_id` は送らない（旧 GCM の再利用は分布・前処理がずれるため BE が非推奨）。
   */
  async createEstimationTask(req: {
    model_id: string;
    input_file: { storage_type: string; saved_path: string };
    method_config: { samples_per_profile: number };
    /** 選択バイアス調整を反映する場合のみ。`ipw/compute` の戻り値。 */
    ipw_run_id?: string;
  }): Promise<EstimationTaskDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimationTasks}`;
    return (await apiClient.post({
      url,
      body: req,
      useToken: true,
    })) as EstimationTaskDef;
  }

  /** 推定タスクの状態を引く。`status === "finished"` で estimation_id が入る。 */
  async fetchEstimationTask(taskId: string): Promise<EstimationTaskDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimationTask(taskId)}`;
    return (await apiClient.get({ url, useToken: true })) as EstimationTaskDef;
  }

  /** 推定結果のメタデータ。 */
  async fetchEstimation(estimationId: string): Promise<EstimationDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimation(estimationId)}`;
    return (await apiClient.get({ url, useToken: true })) as EstimationDef;
  }

  /** 推定結果の行。 */
  async fetchEstimationRecords(
    estimationId: string
  ): Promise<EstimationRecordsDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimationRecords(estimationId)}`;
    return (await apiClient.get({ url, useToken: true })) as EstimationRecordsDef;
  }

  /** 推定結果の CSV。 */
  async fetchEstimationDownload(estimationId: string): Promise<Blob> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.estimationDownload(estimationId)}`;
    return (await apiClient.get({
      url,
      useToken: true,
      responseType: "blob",
    })) as unknown as Blob;
  }

  /**
   * 共変量バランス（SMD）を取得する。
   *
   * `ipw_run_id` を付けると、選択バイアス調整後の SMD が返る（省略時は調整前）。
   */
  async fetchSmdBalance(req: {
    asset_id: string;
    upload_id: string;
    ipw_run_id?: string;
  }): Promise<SmdBalanceDef> {
    const query = new URLSearchParams(
      // undefined の ipw_run_id を "undefined" という文字列で送らない
      Object.entries(req).filter(([, value]) => !!value) as [string, string][]
    ).toString();
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.smdBalance}?${query}`;
    return (await apiClient.get({ url })) as SmdBalanceDef;
  }

  /** 選択バイアス調整（IPW）を計算する。返った ipw_run_id で SMD を引き直す。 */
  async computeIpw(req: {
    asset_id: string;
    upload_id: string;
  }): Promise<IpwComputeDef> {
    const url = `${getPlatformUrl()}${SIMULATION_PATHS.ipwCompute}`;
    return (await apiClient.post({
      url,
      body: req,
      useToken: true,
    })) as IpwComputeDef;
  }
}
